import type { PropsWithChildren } from 'react';
import { Children, useRef } from 'react';
import {
  A11y,
  Keyboard,
  Navigation as NavigationModule,
  Scrollbar,
} from 'swiper';
import { SwiperSlide } from 'swiper/react';

import type { ProductTypeOptions } from '@/types/product';

import { Visibility } from '@/components/Visibility';
import { usePhotoSwipe } from '@/hooks/usePhotoSwipe';

import { useCompoundContext } from '../context';
import { Navigation } from './Navigation';

import * as GS from '../ImageCarousel.styles';

interface CarouselProps
  extends PropsWithChildren, Partial<Pick<HTMLElement, 'id' | 'className'>> {
  productType: ProductTypeOptions;
}

export const Carousel = ({
  children,
  productType,
  ...props
}: CarouselProps) => {
  const { gallery, isProcessing } = useCompoundContext();

  usePhotoSwipe(!isProcessing, gallery);

  const hasMultipleSlides = Children.count(children) > 1;
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  return (
    <div data-test-id='CarouselContainer' id={gallery}>
      <GS.StyledSwiper
        type={productType}
        {...props}
        data-test-id='CarouselSwiper'
        keyboard={{ enabled: true, onlyInViewport: false }}
        loop
        modules={[Keyboard, Scrollbar, NavigationModule, A11y]}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        scrollbar={{
          draggable: true,
          hide: !hasMultipleSlides,
        }}
      >
        {Children.map(children, (child, index) => (
          <SwiperSlide data-test-id={`CarouselSwiperSlide-${index}`}>
            {child}
          </SwiperSlide>
        ))}

        <Visibility xsHidden={!hasMultipleSlides}>
          <Navigation
            ref={nextRef}
            data-test-id='CarouselNavigationNext'
            type='next'
          />
          <Navigation
            ref={prevRef}
            data-test-id='CarouselNavigationPrev'
            type='prev'
          />
        </Visibility>
      </GS.StyledSwiper>
    </div>
  );
};
